import _ from 'lodash'

import { store } from '_/vuex/store'

const multipleOptionsTypes = ['assets', 'relationship', 'one_to_many']

/**
 * Checks if field should be disabled
 *
 * @param   {object}  moduleField
 * @param   {object}  Entry
 * @return  {boolean}
 */
const checkIfFieldDisabled = ({ Entry, moduleField }) => {
  // If disabled is passed as field option, disable field
  if (moduleField.disabled) {
    return true
  }

  // If in create entry mode and current field is in the edit restricted array for this module, disable field
  if (
    Entry.admin.editorMode === 'create' &&
    _.has(Entry.admin, 'selectedModule.permission_control.edit_restrictions') &&
    Entry.admin.selectedModule.permission_control.edit_restrictions.indexOf(
      moduleField.unique_name
    ) > -1
  ) {
    return true
  }

  // If in edit entry mode and current field is in the edit restricted array for this entry, disable field
  if (
    Entry.admin.editorMode === 'edit' &&
    _.has(
      Entry.admin,
      'editedEntry.system.permission_control.edit_restrictions'
    ) &&
    Entry.admin.editedEntry.system.permission_control.edit_restrictions.indexOf(
      moduleField.unique_name
    ) > -1
  ) {
    return true
  }

  // If in edit entry mode and entry's permissions contain update:false, disable field
  if (
    Entry.admin.editorMode === 'edit' &&
    _.has(Entry.admin, 'editedEntry.system.permission_control.crud.update') &&
    !Entry.admin.editedEntry.system.permission_control.crud.update
  ) {
    return true
  }

  // If in edit entry mode and this field is not an editable field as defined by module field setting, disable field
  if (Entry.admin.editorMode === 'edit' && !moduleField.editable) {
    return true
  }

  return false
}

/**
 * Generate field options
 *
 * @param   {object}  moduleField
 * @param   {object}  Entry
 * @param   {function}  customCallback
 * @return  {object}
 */
const makeField = (moduleField, Entry, customCallback) => {
  const fieldType = Object.assign(
    {},
    store.state.photonField.fieldTypes[moduleField.type]
  )

  const relatedTableName = getRelatedTableName({
    fieldType: fieldType.type,
    Entry,
    moduleField,
  })

  const relatedTableNames = getRelatedTableNames({
    fieldType: fieldType.type,
    Entry,
    moduleField,
  })

  const fieldOptions = {
    canCreateSearchChoice: moduleField.can_create_search_choice,
    defaultValue: moduleField.default,
    disabled: checkIfFieldDisabled({
      Entry,
      moduleField,
    }),
    flattenToOptgroups: moduleField.flatten_to_optgroups,
    hidden: moduleField.hidden,
    id: moduleField.id,
    isActiveEntryFilter: moduleField.is_active_entry_filter,
    copyAsReference: moduleField.copy_as_reference,
    isSystem: moduleField.is_system,
    label: moduleField.name[store.state.ui.defaultLocale.languageCode],
    lazyLoading: moduleField.lazy_loading,
    preselectFirst: moduleField.preselect_first,
    moduleName:
      Entry.admin.selectedModule.name[
        store.state.ui.defaultLocale.languageCode
      ],
    moduleTableName: Entry.admin.selectedModule.table_name,
    multiple:
      _.indexOf(multipleOptionsTypes, fieldType.type) > -1 ? true : false,
    mutation: `${Entry.admin.moduleName}/UPDATE_MODULE_ENTRY_FIELD`,
    name: moduleField.unique_name,
    presentAsListed: moduleField.present_as_listed,
    presentAsReference: moduleField.present_as_reference,
    showLinkExistingEntries: moduleField.show_link_existing_entries,
    showCreateNewEntries: moduleField.show_create_new_entries,
    relatedEntry:
      Entry.admin.editedEntry[moduleField.unique_name + '_related_entry'],
    relatedTableName,
    relatedTableNames,
    required:
      moduleField.validation_rules && isRequired(moduleField.validation_rules),
    supportsTranslation: fieldType.supports_translation,
    tooltip:
      moduleField.tooltip_text[store.state.ui.defaultLocale.languageCode],
    translatable: moduleField.translatable,
    value: fetchValue(Entry, moduleField.unique_name),
    validationRules: moduleField.validation_rules,
    vueComponent: fieldType.component,
  }

  // If a custom callback function was defined execute it here
  if (typeof customCallback === 'function') {
    customCallback(moduleField, fieldOptions, Entry)
  }

  return fieldOptions
}

/**
 * Fetches the value
 *
 * @param   {object}  entry
 * @param   {string}  moduleFieldUniqueName
 * @param   {string}  relatedTableName
 * @param   {string}  component
 * @return  {mixed}
 */
function fetchValue(entry, moduleFieldUniqueName) {
  let uniqueName = moduleFieldUniqueName

  return _.has(entry.admin.editedEntry.fields, uniqueName)
    ? entry.admin.editedEntry.fields[uniqueName]
    : null
}

/**
 * Gets a related table name
 *
 * @param   {object}  options.Entry
 * @param   {string}  options.fieldTypeName
 * @param   {object}  options.moduleField
 * @return  {string}
 */
function getRelatedTableName({ Entry, fieldType, moduleField }) {
  if (!_.isEmpty(moduleField.related_modules)) {
    // Gets related table name for module related to the current field (just to shorten syntax in options)
    return Entry.photonModule.moduleIdToTableNameMap[
      _.head(moduleField.related_modules)
    ]
  }

  if (fieldType === 'file' || fieldType === 'files') {
    // Related table is hard-coded to assets (the only module containing file assets)
    return 'assets'
  }

  return null
}

/**
 * Gets a related table name
 *
 * @param   {object}  options.Entry
 * @param   {string}  options.fieldTypeName
 * @param   {object}  options.moduleField
 * @return  {string}
 */
function getRelatedTableNames({ Entry, fieldType, moduleField }) {
  if (!_.isEmpty(moduleField.related_modules)) {
    // Gets related table name for module related to the current field (just to shorten syntax in options)
    return moduleField.related_modules.map(
      relatedModule => Entry.photonModule.moduleIdToTableNameMap[relatedModule]
    )
  }

  return []
}

/**
 * Explode string rules and
 *
 * @param {string} rulesString
 * @return  {boolean}
 */
function isRequired(rulesString) {
  let rulesArray = rulesString.split('|')
  return rulesArray.some(rule => {
    return rule === 'required'
  })
}

function getLocales(field) {
  if (!field.translatable) {
    return [_.find(store.state.ui.locales, { default: true })]
  }

  return store.state.ui.locales
}

function generateId(locale, field) {
  return `${field.id}-${locale.languageCode}`
}

function generateName(locale, field) {
  return `${field.unique_name}-${locale.languageCode}`
}

/**
 * Dynamically creates fields from Entry.admin.selectedModule.fields
 *
 * @param   {object}  Entry
 * @param   {function}  customCallback
 * @return  {object}
 */
export const getEntryFields = (Entry, customCallback) => {
  if (!Entry.admin.selectedModule || !Entry.admin.selectedModule.fields) {
    return []
  }

  return Entry.admin.selectedModule.fields.map(field => {
    return makeField(field, Entry, customCallback)
  })
}
